import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { IssueFragment, UnitType } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { formatQuantity } from "../Containers/containerDisplay";
import {
  CREATE_WASTE_SHIPMENT_MUTATION,
  FACILITIES_WITH_SHIPPABLE_CONTAINERS_QUERY,
  SHIPPABLE_CONTAINERS_QUERY,
  VALIDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { daysUntil } from "./shipmentDisplay";
import { groupContainersByStream } from "./shippableContainerGrouping";
import {
  ShippableContainerGroup,
  ShippableContainerHeader,
  ShippableContainerListSkeleton,
} from "./ShippableContainerRow";
import { useContainerSelection } from "./useContainerSelection";

type FacilityOption = { id: string; name: string };

const SOON_DUE_DAYS = 30;

/**
 * The picker is a hand-built row layout rather than a table, so its header and
 * its rows have to agree on column widths independently. Keeping the widths
 * here means widening a column can't silently misalign the label above it.
 *
 * The header's leading spacer stands in for each row's `size="small"` Checkbox:
 * a 20px icon inside 9px of padding on each side.
 */

export function PackageShipmentBuilder() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const alerts = useAlerts();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");

  const { selectedIds, toggle, setSelection, clear } = useContainerSelection();

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    { label: "Package a shipment" },
  ]);

  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: facilityId ?? "" },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });
  const containers = useMemo(
    () => data?.facility?.shippableContainers?.items ?? [],
    [data?.facility?.shippableContainers?.items]
  );
  // While switching facilities the query has no cached result for the new
  // facility yet, so `data` is briefly undefined. Treat that as loading (not
  // "0 containers") so the count/empty-state don't flash a stale zero.
  const loadingContainers = loading && data?.facility == null;

  // Only facilities that actually have something to ship, so the picker
  // never lands the user on an empty facility.
  const { data: facilitiesData, loading: facilitiesLoading } = useQuery(
    FACILITIES_WITH_SHIPPABLE_CONTAINERS_QUERY,
    { variables: { tenantId: tenantId ?? "" }, skip: !tenantId }
  );
  const facilityOptions: FacilityOption[] =
    facilitiesData?.tenant?.facilitiesWithShippableContainers ?? [];
  const selectedFacility = facilityOptions.find((f) => f.id === facilityId) ?? null;
  const facilityName = selectedFacility?.name ?? data?.facility?.name;

  // Warn-level validation (e.g. a picked container already on another open
  // shipment) surfaces live as the selection changes; CRITICAL rules are
  // enforced by the mutation itself.
  const [validate, { data: validationData, previousData: prevValidationData }] = useLazyQuery(
    VALIDATE_WASTE_SHIPMENT_INPUT,
    { fetchPolicy: "no-cache" }
  );
  useEffect(() => {
    if (facilityId && selectedIds.size > 0) {
      validate({ variables: { input: { facilityId, containerIds: [...selectedIds] } } });
    }
  }, [facilityId, selectedIds, validate]);
  // Fall back to the prior result while a re-validation is in flight so the
  // critical-issue alert stays mounted and only its text updates — otherwise
  // the no-cache query blanks data mid-flight and the alert flashes.
  const effectiveValidation = validationData ?? prevValidationData;
  const validationIssues: IssueFragment[] =
    selectedIds.size > 0 ? (effectiveValidation?.wasteShipmentInputValidator ?? []) : [];
  const criticalIssues = validationIssues.filter((issue) => issue.level === "CRITICAL");

  const [createShipment, { loading: creating }] = useMutation(CREATE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: ["TenantWasteShipments", "FacilityWasteShipments"],
  });

  const groups = useMemo(() => groupContainersByStream(containers), [containers]);
  const soonDueCount = useMemo(
    () =>
      containers.filter((c) => {
        const days = daysUntil(c.clock.effectiveDeadlineDate);
        return days !== null && days <= SOON_DUE_DAYS;
      }).length,
    [containers]
  );

  const selected = useMemo(
    () => containers.filter((c) => selectedIds.has(c.id)),
    [containers, selectedIds]
  );

  // Distinct streams on the request — a stream split across units still
  // counts once, so the header never disagrees with what the user selected.
  const selectedStreamCount = useMemo(
    () => new Set(selected.map((c) => c.wasteStream?.id ?? "")).size,
    [selected]
  );

  // The live "manifest request" summary: selected containers rolled up by
  // stream + unit, mirroring the backend's grouping — shown compactly in the
  // summary card so the forming request is legible before creating.
  const requestLines = useMemo(() => {
    const lines = new Map<
      string,
      { streamName: string | null; count: number; total: number; unit: UnitType | null }
    >();
    for (const container of selected) {
      const unit = container.amountUnit ?? container.capacityUnit;
      const key = `${container.wasteStream?.id ?? ""}|${unit ?? ""}`;
      const line = lines.get(key) ?? {
        streamName: container.wasteStream?.name ?? null,
        count: 0,
        total: 0,
        unit: unit ?? null,
      };
      line.count += 1;
      line.total += container.amount ?? 0;
      lines.set(key, line);
    }
    return [...lines.values()].sort((a, b) =>
      (a.streamName ?? "￿").localeCompare(b.streamName ?? "￿")
    );
  }, [selected]);

  const previewStreamSummary = useMemo(() => {
    const shown = requestLines.slice(0, 3).map((line) => {
      const qty = line.unit ? formatQuantity(line.total, line.unit) : `${line.count} cont`;
      return `${line.streamName ?? "Uncharacterized"} ${qty}`;
    });
    const more = requestLines.length - shown.length;
    return shown.join(" · ") + (more > 0 ? ` · +${more} more` : "");
  }, [requestLines]);

  // Master checkbox: checked when every ready container is selected,
  // indeterminate when only some are.
  const allSelected = containers.length > 0 && containers.every((c) => selectedIds.has(c.id));
  const someSelected = containers.some((c) => selectedIds.has(c.id));

  const handleFacilityChange = (facility: { id: string } | null) => {
    const next = new URLSearchParams(searchParams);
    if (facility) {
      next.set("facilityId", facility.id);
    } else {
      next.delete("facilityId");
    }
    setSearchParams(next, { replace: true });
    clear();
  };

  const handleCreate = async () => {
    if (!facilityId || selectedIds.size === 0) {
      return;
    }
    try {
      const result = await createShipment({
        variables: { input: { facilityId, containerIds: [...selectedIds] } },
      });
      const created = result.data?.createWasteShipment;
      alerts.success(`Shipment ${created?.manifestRequestNumber ?? ""} created.`);
      if (created) {
        // Header details (vendor, transporter, TSDF, dates) are filled in on
        // the shipment itself — land there.
        navigate(`/o/${tenantId}/waste/shipments/${created.id}`);
      }
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to create the shipment.");
    }
  };

  if (!tenantId) {
    return null;
  }

  const facilitySelect = (
    <Autocomplete
      options={facilityOptions}
      getOptionLabel={(o: FacilityOption) => o.name}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      value={selectedFacility}
      onChange={(_e, value) => handleFacilityChange(value)}
      loading={facilitiesLoading}
      fullWidth
      renderInput={(params) => <TextField {...params} label="Facility" size="small" />}
      noOptionsText="No facilities have containers ready to ship"
    />
  );

  return (
    <Stack spacing={2} sx={{ py: 2 }}>
      <Box sx={{ px: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Shipment{facilityName ? ` · ${facilityName}` : ""}
        </Typography>
        <Typography variant="h5">Package a shipment</Typography>
        <Typography variant="body2" color="text.secondary">
          Select what&apos;s leaving central accumulation. Encamp builds a manifest request for your
          vendor — it isn&apos;t the official manifest.
        </Typography>
      </Box>

      {!facilityId && (
        <Box sx={{ px: 3, display: "flex", justifyContent: "center", pt: 4 }}>
          <Paper
            variant="outlined"
            sx={{ p: 4, maxWidth: 460, width: "100%", textAlign: "center" }}
          >
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              Which facility is shipping?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Only facilities with containers ready to ship are listed.
            </Typography>
            {facilitySelect}
          </Paper>
        </Box>
      )}

      {facilityId && <Box sx={{ px: 3, maxWidth: 420 }}>{facilitySelect}</Box>}

      {facilityId && soonDueCount > 0 && (
        <Box sx={{ px: 3 }}>
          <Alert severity="warning">
            <strong>
              {soonDueCount} container{soonDueCount === 1 ? " is" : "s are"} due within 30 days.
            </strong>{" "}
            {soonDueCount === 1
              ? "Ship it to stay compliant."
              : "Ship them together to stay compliant and minimize hauls."}
          </Alert>
        </Box>
      )}

      {facilityId && (
        <Box sx={{ px: 3 }}>
          <Paper elevation={0} sx={{ py: 2 }}>
            {/* px matches the group headers and rows so the master checkbox
                lines up in the same column as the ones below it. */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, px: 1 }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                disabled={loadingContainers || containers.length === 0}
                onChange={(e) =>
                  setSelection(
                    containers.map((c) => c.id),
                    e.target.checked
                  )
                }
                inputProps={{ "aria-label": "Select all ready containers" }}
              />
              {loadingContainers ? (
                <Skeleton variant="text" width={260} />
              ) : (
                <Typography variant="subtitle1">
                  {containers.length} container{containers.length === 1 ? "" : "s"} ready in central
                  accumulation
                </Typography>
              )}
            </Stack>

            {loadingContainers && <ShippableContainerListSkeleton />}

            {!loadingContainers && containers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No central-accumulation containers are ready to ship at this facility.
              </Typography>
            )}

            {!loadingContainers && containers.length > 0 && <ShippableContainerHeader />}

            {groups.map((group) => (
              <ShippableContainerGroup
                key={group.streamId || "uncharacterized"}
                group={group}
                selectedIds={selectedIds}
                onToggle={toggle}
                onToggleGroup={setSelection}
              />
            ))}
          </Paper>
        </Box>
      )}

      {facilityId && containers.length > 0 && (
        <Box sx={{ px: 3 }}>
          <Paper elevation={0} sx={{ py: 2 }}>
            {/* Open-shipment double-packaging shows per row (warning icon);
                only the rare blocking issues surface here as a safety net. */}
            {criticalIssues.length > 0 && (
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {criticalIssues.map((issue, i) => (
                  <Alert key={`crit-${issue.key}-${i}`} severity="error">
                    {issue.message}
                  </Alert>
                ))}
              </Stack>
            )}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              spacing={2}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">
                  {selectedIds.size} container{selectedIds.size === 1 ? "" : "s"} ·{" "}
                  {selectedStreamCount} stream{selectedStreamCount === 1 ? "" : "s"}
                  {previewStreamSummary ? ` · ${previewStreamSummary}` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Not an official manifest — your vendor creates and returns that. Vendor,
                  transporter, and dates are added on the shipment after it&apos;s created.
                </Typography>
              </Box>
              <Button
                variant="contained"
                disabled={selectedIds.size === 0 || creating}
                onClick={handleCreate}
                // The label must not wrap, so the button must not be allowed to
                // shrink under it either — between the sm breakpoint and about
                // 700px the summary text would otherwise squeeze it and the
                // label would spill outside its own background.
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                Create draft shipment
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </Stack>
  );
}
